import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { useExperimentContext } from '@/experimentation'
import { DiscoverItemInteraction, useContentInteraction } from '@/services/Analytics/ContentInteractions'
import { FeaturedProject } from '@/types/codegen-contentful'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { FeaturedProjectBackground } from './FeaturedProjectBackground'
import { FeaturedProjectCallToAction } from './FeaturedProjectCallToAction'
import { FeaturedProjectExperimentWrapper } from './FeaturedProjectExperimentWrapper'
import { assignBackgroundImage } from './FeaturedProjectUtils'
import { FeaturedProjectVideoBackground } from './FeaturedProjectVideoBackground'

export interface FeaturedProjectProps {
  featuredProject: FeaturedProject
  containerPosition: number
  federatedContent?: {
    id: string
    __typename: string
  }
}

export const FeaturedProjectDisplay: React.FC<FeaturedProjectProps> = ({
  featuredProject,
  containerPosition,
  federatedContent,
}) => {
  const { trackInteraction } = useContentInteraction()
  const {
    backgroundImageExperiment,
    ctaPosition,
    desktopBackgroundUrl,
    mobileBackgroundUrl,
    tabletBackgroundUrl,
    ultrawideBackgroundUrl,
    projectSlug,
    showGuildCta,
    showTicketsCta,
  } = featuredProject

  const { asPath } = useSafeRouter()
  const { locale } = useLocale()
  const { getFeatureValue, ready } = useExperimentContext()
  const iWasAStranger = projectSlug === slugs.iWasAStranger

  const showIWasAStrangerVideo = useMemo(() => {
    if (!iWasAStranger || !ready) return false
    return getFeatureValue('i-was-a-stranger-featured-project-video-background', false) ?? false
  }, [iWasAStranger, getFeatureValue, ready])

  const tablet = tabletBackgroundUrl ?? desktopBackgroundUrl
  const showDavidVideo = projectSlug === slugs.david
  const pathWithoutQuery = asPath.split('?')[0] ?? asPath
  const normalizedPath = locale ? pathWithoutQuery.replace(`/${locale}`, '') : pathWithoutQuery
  const isWatchLanding = normalizedPath === paths.watch.index

  const backgrounds = {
    mobile: mobileBackgroundUrl as string,
    tablet: tablet as string,
    desktop: desktopBackgroundUrl as string,
    ultra: ultrawideBackgroundUrl as string,
    gradient: true,
    gravity: 'g_face',
  }

  const backgroundImage = assignBackgroundImage(backgrounds)
  const shorterDisplay = asPath.includes('/watch/dry-bar-best-of')
  const backgroundVideo =
    'https://content.angelstudios.com/david/David_tLoopingVideo_l-en_wNOCAPS_16x9_f23_y20251013_iDG.mp4'
  const IWASBackgroundVideo =
    'https://content.angelstudios.com/i-was-a-stranger/I+Was+A+Stranger_tLoopingVideo_20s_l-en_wNOCAPS_16x9_f23_y20250923_iAA.mp4'

  // Track content interaction for featured project - interstitial
  const contentInteraction = useMemo(() => {
    // Determine the placement container based on CTA type
    const placement = showGuildCta ? 'interstitial_guild' : showTicketsCta ? 'interstitial_theatrical' : null

    if (!placement || !federatedContent) return null
    const contentId = federatedContent?.id ?? featuredProject.sys.id
    const contentType = federatedContent?.__typename as 'ContentSeries' | 'ContentMovie' | 'ContentSpecial'

    return new DiscoverItemInteraction({
      id: contentId,
      __typename: contentType,
      attribution: null,
    })
      .withSurface(isWatchLanding ? 'discover' : 'generic_list')
      .withPlacement(placement, containerPosition, 0, 'single_feature')
      .withImageArtwork(
        showDavidVideo ? backgroundVideo : showIWasAStrangerVideo ? IWASBackgroundVideo : desktopBackgroundUrl ?? null,
        'landscape_display',
      )
  }, [
    showGuildCta,
    showTicketsCta,
    federatedContent,
    featuredProject.sys.id,
    isWatchLanding,
    containerPosition,
    showDavidVideo,
    showIWasAStrangerVideo,
    desktopBackgroundUrl,
  ])

  const hasTrackedImpressionRef = useRef(false)
  const [isInView, setIsInView] = useState(false)

  const handleInViewChange = useCallback((inView: boolean) => {
    setIsInView(inView)
  }, [])

  useEffect(() => {
    if (isInView && contentInteraction && !hasTrackedImpressionRef.current) {
      trackInteraction(contentInteraction.withImpression())
      hasTrackedImpressionRef.current = true
    }
  }, [contentInteraction, trackInteraction, isInView])

  return (
    <InView onChange={handleInViewChange} triggerOnce threshold={0.65}>
      {!showDavidVideo && !showIWasAStrangerVideo && (
        <div
          className={classNames(
            'relative bg-transparent',
            shorterDisplay
              ? 'h-[70vh] lg:min-h-[400px] lg:h-[675px] lg:max-w-[1200px] lg:mx-auto lg:rounded-2xl lg:overflow-hidden'
              : 'h-[90vh] lg:min-h-[1000px] shortDesktop:min-h-[800px]',
          )}
        >
          {backgroundImageExperiment ? (
            <FeaturedProjectExperimentWrapper featuredProject={featuredProject} />
          ) : (
            <div className="relative h-full w-full">
              <FeaturedProjectBackground backgroundImage={backgroundImage} />
              <FeaturedProjectCallToAction
                featuredProject={featuredProject}
                position={ctaPosition ?? 'left'}
                shorterDisplay={shorterDisplay}
                contentInteraction={contentInteraction}
              />
            </div>
          )}
        </div>
      )}
      {(showDavidVideo || showIWasAStrangerVideo) && (
        <div
          className={classNames(
            'relative bg-transparent',
            shorterDisplay
              ? 'h-[70vh] lg:min-h-[400px] lg:h-[675px] lg:max-w-[1200px] lg:mx-auto lg:rounded-2xl lg:overflow-hidden'
              : 'h-[80vh] lg:min-h-[850px] shortDesktop:min-h-[600px]',
          )}
        >
          {backgroundImageExperiment ? (
            <FeaturedProjectExperimentWrapper featuredProject={featuredProject} />
          ) : (
            <div className="relative h-full w-full">
              <FeaturedProjectVideoBackground
                backgroundVideo={showIWasAStrangerVideo ? IWASBackgroundVideo : backgroundVideo}
              />
              <FeaturedProjectCallToAction
                featuredProject={featuredProject}
                position={ctaPosition ?? 'left'}
                shorterDisplay={false}
                videoBackground
                contentInteraction={contentInteraction}
                projectSlug={projectSlug}
              />
            </div>
          )}
        </div>
      )}
    </InView>
  )
}
